'use client';
import React, { useState, useMemo } from 'react';
import Icon from '@/components/ui/AppIcon';
import CreatorResultCard from './CreatorResultCard';
import FilterPanel from './FilterPanel';
import ComparisonDrawer from './ComparisonDrawer';

export type Creator = {
  id: string;
  name: string;
  handle: string;
  category: string[];
  platforms: ('instagram' | 'tiktok' | 'youtube' | 'snapchat')[];
  city: string;
  wilaya: string;
  gender: 'F' | 'M';
  ageRange: string;
  languages: string[];
  followers: number;
  followersDisplay: string;
  avgViews: string;
  engagementRate: number;
  audienceAlgeria: number;
  audienceQuality: number;
  momentumScore: number;
  fitScore: number;
  verified: boolean;
  available: boolean;
  budget: number;
  avatar: string;
  avatarAlt: string;
  contentTypes: string[];
  skills: string[];
};

const allCreators: Creator[] = [
{
  id: 'fc-yasmine', name: 'Yasmine Belkacem', handle: '@yasmine.create',
  category: ['Lifestyle', 'Beauty'], platforms: ['instagram', 'tiktok', 'snapchat'],
  city: 'Alger', wilaya: 'Alger', gender: 'F', ageRange: '25-34',
  languages: ['Arabe', 'Darija', 'Français'],
  followers: 284000, followersDisplay: '284K', avgViews: '740K',
  engagementRate: 8.7, audienceAlgeria: 78, audienceQuality: 91,
  momentumScore: 94, fitScore: 94, verified: true, available: true,
  budget: 180000, contentTypes: ['Reel', 'Story', 'UGC'],
  skills: ['Reels Production', 'UGC', 'Acting'],
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e58b4ded-1772313159851.png",
  avatarAlt: 'Yasmine Belkacem, Algerian lifestyle and beauty creator'
},
{
  id: 'fc-lina', name: 'Lina Meziane', handle: '@lina.beauty',
  category: ['Beauty', 'Fashion'], platforms: ['instagram', 'tiktok', 'snapchat'],
  city: 'Constantine', wilaya: 'Constantine', gender: 'F', ageRange: '22-28',
  languages: ['Arabe', 'Darija', 'Français'],
  followers: 317000, followersDisplay: '317K', avgViews: '1.1M',
  engagementRate: 9.4, audienceAlgeria: 82, audienceQuality: 88,
  momentumScore: 91, fitScore: 89, verified: true, available: false,
  budget: 220000, contentTypes: ['Reel', 'TikTok'],
  skills: ['Makeup', 'Storytelling', 'Reels Production'],
  avatar: "https://images.unsplash.com/photo-1725759908147-9a26b5bd4581",
  avatarAlt: 'Lina Meziane, beauty and fashion creator from Constantine'
},
{
  id: 'fc-sarah', name: 'Sarah Hamdi', handle: '@sarah.h',
  category: ['Lifestyle', 'Family'], platforms: ['instagram', 'tiktok'],
  city: 'Oran', wilaya: 'Oran', gender: 'F', ageRange: '28-35',
  languages: ['Arabe', 'Darija'],
  followers: 182000, followersDisplay: '182K', avgViews: '390K',
  engagementRate: 7.2, audienceAlgeria: 91, audienceQuality: 86,
  momentumScore: 87, fitScore: 84, verified: true, available: true,
  budget: 120000, contentTypes: ['Reel', 'Story', 'UGC'],
  skills: ['Storytelling', 'Product Demonstration', 'UGC'],
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10e113847-1772313149919.png",
  avatarAlt: 'Sarah Hamdi, lifestyle and family creator from Oran'
},
{
  id: 'fc-karim', name: 'Karim Oussedik', handle: '@karim.tech',
  category: ['Tech', 'Business'], platforms: ['instagram', 'youtube'],
  city: 'Alger', wilaya: 'Alger', gender: 'M', ageRange: '28-38',
  languages: ['Arabe', 'Français', 'Anglais'],
  followers: 94000, followersDisplay: '94K', avgViews: '210K',
  engagementRate: 5.9, audienceAlgeria: 74, audienceQuality: 83,
  momentumScore: 79, fitScore: 77, verified: true, available: true,
  budget: 90000, contentTypes: ['Reel', 'YouTube'],
  skills: ['Product Demonstration', 'Scripting', 'Interview'],
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b65d6848-1763294493812.png",
  avatarAlt: 'Karim Oussedik, tech and business creator from Alger'
},
{
  id: 'fc-amel', name: 'Amel Bensalem', handle: '@amel.bensalem',
  category: ['Beauty'], platforms: ['instagram', 'tiktok'],
  city: 'Alger', wilaya: 'Alger', gender: 'F', ageRange: '20-26',
  languages: ['Darija', 'Français'],
  followers: 156000, followersDisplay: '156K', avgViews: '480K',
  engagementRate: 9.1, audienceAlgeria: 85, audienceQuality: 87,
  momentumScore: 88, fitScore: 91, verified: true, available: true,
  budget: 140000, contentTypes: ['Reel', 'TikTok', 'UGC'],
  skills: ['Makeup', 'UGC', 'Reels Production'],
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_153e97c82-1763293683213.png",
  avatarAlt: 'Amel Bensalem, beauty creator from Alger'
},
{
  id: 'fc-sofiane', name: 'Sofiane Rahmani', handle: '@sofiane.food',
  category: ['Food'], platforms: ['instagram', 'tiktok'],
  city: 'Alger', wilaya: 'Alger', gender: 'M', ageRange: '25-32',
  languages: ['Darija', 'Arabe'],
  followers: 73000, followersDisplay: '73K', avgViews: '180K',
  engagementRate: 7.8, audienceAlgeria: 93, audienceQuality: 80,
  momentumScore: 82, fitScore: 76, verified: false, available: true,
  budget: 65000, contentTypes: ['Reel', 'Story'],
  skills: ['Food Styling', 'Reels Production', 'Storytelling'],
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10558c88b-1783550521466.png",
  avatarAlt: 'Sofiane Rahmani, food content creator from Alger'
},
{
  id: 'fc-nadia', name: 'Nadia Cherif', handle: '@nadia.fashion',
  category: ['Fashion', 'Lifestyle'], platforms: ['instagram', 'tiktok'],
  city: 'Alger', wilaya: 'Alger', gender: 'F', ageRange: '22-30',
  languages: ['Français', 'Darija'],
  followers: 209000, followersDisplay: '209K', avgViews: '520K',
  engagementRate: 6.4, audienceAlgeria: 71, audienceQuality: 78,
  momentumScore: 76, fitScore: 72, verified: true, available: true,
  budget: 160000, contentTypes: ['Reel', 'Story'],
  skills: ['Fashion Styling', 'Photography', 'Reels Production'],
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18354537e-1787082903492.png",
  avatarAlt: 'Nadia Cherif, fashion creator from Alger'
},
{
  id: 'fc-bilal', name: 'Bilal Mansouri', handle: '@bilal.gaming',
  category: ['Gaming'], platforms: ['instagram', 'tiktok', 'youtube'],
  city: 'Alger', wilaya: 'Alger', gender: 'M', ageRange: '18-26',
  languages: ['Arabe', 'Darija', 'Anglais'],
  followers: 41000, followersDisplay: '41K', avgViews: '95K',
  engagementRate: 5.2, audienceAlgeria: 88, audienceQuality: 72,
  momentumScore: 73, fitScore: 68, verified: false, available: true,
  budget: 40000, contentTypes: ['Reel', 'YouTube'],
  skills: ['Gaming Commentary', 'Reels Production'],
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b8c67627-1763295469530.png",
  avatarAlt: 'Bilal Mansouri, gaming content creator'
},
{
  id: 'fc-rania', name: 'Rania Touati', handle: '@rania.lifestyle',
  category: ['Lifestyle'], platforms: ['instagram', 'snapchat'],
  city: 'Annaba', wilaya: 'Annaba', gender: 'F', ageRange: '24-32',
  languages: ['Arabe', 'Darija'],
  followers: 128000, followersDisplay: '128K', avgViews: '310K',
  engagementRate: 8.3, audienceAlgeria: 89, audienceQuality: 84,
  momentumScore: 71, fitScore: 73, verified: true, available: true,
  budget: 100000, contentTypes: ['Reel', 'Story', 'UGC'],
  skills: ['Storytelling', 'UGC', 'Photography'],
  avatar: "https://images.unsplash.com/photo-1517467152965-7a3d898a653c",
  avatarAlt: 'Rania Touati, lifestyle creator from Annaba'
},
{
  id: 'fc-imane', name: 'Imane Kaci', handle: '@imane.kaci',
  category: ['Beauty', 'UGC'], platforms: ['instagram', 'tiktok'],
  city: 'Blida', wilaya: 'Blida', gender: 'F', ageRange: '20-26',
  languages: ['Darija', 'Arabe'],
  followers: 22000, followersDisplay: '22K', avgViews: '68K',
  engagementRate: 14.8, audienceAlgeria: 95, audienceQuality: 76,
  momentumScore: 64, fitScore: 71, verified: false, available: true,
  budget: 25000, contentTypes: ['UGC', 'Reel'],
  skills: ['UGC', 'Reels Production'],
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17ef1722a-1766610746057.png",
  avatarAlt: 'Imane Kaci, UGC and beauty creator from Blida'
},
{
  id: 'fc-omar', name: 'Omar Hadjadj', handle: '@omar.edu',
  category: ['Education', 'Business'], platforms: ['instagram', 'youtube'],
  city: 'Sétif', wilaya: 'Sétif', gender: 'M', ageRange: '26-35',
  languages: ['Arabe', 'Français'],
  followers: 31000, followersDisplay: '31K', avgViews: '74K',
  engagementRate: 11.2, audienceAlgeria: 91, audienceQuality: 79,
  momentumScore: 61, fitScore: 65, verified: false, available: true,
  budget: 35000, contentTypes: ['Reel', 'YouTube'],
  skills: ['Scripting', 'Interview', 'Storytelling'],
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a636e544-1772701790442.png",
  avatarAlt: 'Omar Hadjadj, education and business creator from Sétif'
},
{
  id: 'fc-mehdi', name: 'Mehdi Terki', handle: '@mehdi.sport',
  category: ['Sport', 'Lifestyle'], platforms: ['instagram', 'tiktok'],
  city: 'Annaba', wilaya: 'Annaba', gender: 'M', ageRange: '20-28',
  languages: ['Arabe', 'Darija'],
  followers: 27000, followersDisplay: '27K', avgViews: '62K',
  engagementRate: 10.4, audienceAlgeria: 92, audienceQuality: 74,
  momentumScore: 55, fitScore: 60, verified: false, available: true,
  budget: 30000, contentTypes: ['Reel', 'Story'],
  skills: ['Sport Commentary', 'Reels Production'],
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10fb389f0-1787082903384.png",
  avatarAlt: 'Mehdi Terki, sport and lifestyle creator from Annaba'
}];


export type FilterState = {
  search: string;
  categories: string[];
  platforms: string[];
  gender: string;
  verified: boolean | null;
  available: boolean | null;
  minFollowers: number;
  maxFollowers: number;
  minEngagement: number;
  sortBy: string;
};

const defaultFilters: FilterState = {
  search: '',
  categories: [],
  platforms: [],
  gender: '',
  verified: null,
  available: null,
  minFollowers: 0,
  maxFollowers: 2000000,
  minEngagement: 0,
  sortBy: 'momentum'
};

export default function FindCreatorClient() {
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const filtered = useMemo(() => {
    let result = [...allCreators];

    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter((c) =>
      c.name.toLowerCase().includes(q) ||
      c.handle.toLowerCase().includes(q) ||
      c.category.some((cat) => cat.toLowerCase().includes(q))
      );
    }
    if (filters.categories.length > 0) {
      result = result.filter((c) => filters.categories.some((cat) => c.category.includes(cat)));
    }
    if (filters.platforms.length > 0) {
      result = result.filter((c) => filters.platforms.some((p) => c.platforms.includes(p as Creator['platforms'][0])));
    }
    if (filters.gender) {
      result = result.filter((c) => c.gender === filters.gender);
    }
    if (filters.verified !== null) {
      result = result.filter((c) => c.verified === filters.verified);
    }
    if (filters.available !== null) {
      result = result.filter((c) => c.available === filters.available);
    }
    result = result.filter((c) =>
    c.followers >= filters.minFollowers &&
    c.followers <= filters.maxFollowers &&
    c.engagementRate >= filters.minEngagement
    );

    result.sort((a, b) => {
      if (filters.sortBy === 'momentum') return b.momentumScore - a.momentumScore;
      if (filters.sortBy === 'engagement') return b.engagementRate - a.engagementRate;
      if (filters.sortBy === 'followers') return b.followers - a.followers;
      if (filters.sortBy === 'fitScore') return b.fitScore - a.fitScore;
      return 0;
    });

    return result;
  }, [filters]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < 3 ? [...prev, id] : prev
    );
  };

  const selectedCreators = allCreators.filter((c) => selectedIds.includes(c.id));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-700 text-foreground">Find a Creator</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {filtered.length} créateurs correspondent à vos critères
          </p>
        </div>
        <div className="flex items-center gap-3">
          {selectedIds.length > 0 &&
          <button
            onClick={() => {}}
            className="flex items-center gap-2 bg-primary/15 border border-primary/30 text-primary text-sm font-600 px-4 py-2 rounded-lg transition-all hover:bg-primary/25">
            
              <Icon name="ScaleIcon" size={16} />
              Comparer ({selectedIds.length}/3)
            </button>
          }
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? 'bg-card text-foreground' : 'text-muted-foreground'}`}>
              
              <Icon name="Squares2X2Icon" size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-card text-foreground' : 'text-muted-foreground'}`}>
              
              <Icon name="ListBulletIcon" size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Filter Panel */}
        <FilterPanel filters={filters} onChange={setFilters} onReset={() => setFilters(defaultFilters)} />

        {/* Results */}
        <div className="flex-1 min-w-0 space-y-4">
          {/* Sort Bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Trier par :</span>
              {[
              { id: 'sort-momentum', value: 'momentum', label: 'Momentum' },
              { id: 'sort-engagement', value: 'engagement', label: 'Engagement' },
              { id: 'sort-followers', value: 'followers', label: 'Followers' },
              { id: 'sort-fit', value: 'fitScore', label: 'Brand Fit' }].
              map((s) =>
              <button
                key={s.id}
                onClick={() => setFilters((f) => ({ ...f, sortBy: s.value }))}
                className={`px-3 py-1.5 rounded-lg text-xs font-600 transition-all ${
                filters.sortBy === s.value ?
                'bg-primary text-primary-foreground' :
                'bg-muted text-muted-foreground hover:text-foreground'}`
                }>
                
                  {s.label}
                </button>
              )}
            </div>
            {selectedIds.length > 0 &&
            <button onClick={() => setSelectedIds([])} className="text-xs text-muted-foreground hover:text-foreground">
                Effacer sélection
              </button>
            }
          </div>

          {filtered.length === 0 ?
          <div className="flex flex-col items-center justify-center py-20 bg-card border border-border rounded-xl">
              <Icon name="MagnifyingGlassIcon" size={40} className="text-muted-foreground mb-4" />
              <h3 className="text-base font-700 text-foreground mb-2">Aucun créateur trouvé</h3>
              <p className="text-sm text-muted-foreground text-center max-w-xs">
                Aucun créateur ne correspond à vos filtres. Essayez d'élargir les critères de recherche.
              </p>
              <button
              onClick={() => setFilters(defaultFilters)}
              className="mt-4 px-4 py-2 bg-primary text-primary-foreground text-sm font-600 rounded-lg hover:bg-primary/90 transition-all">
              
                Réinitialiser les filtres
              </button>
            </div> :

          <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4' : 'space-y-3'
          }>
              {filtered.map((creator) =>
            <CreatorResultCard
              key={creator.id}
              creator={creator}
              selected={selectedIds.includes(creator.id)}
              onSelect={() => toggleSelect(creator.id)}
              viewMode={viewMode} />

            )}
            </div>
          }
        </div>
      </div>

      {/* Comparison Drawer */}
      {selectedIds.length >= 2 &&
      <ComparisonDrawer
        creators={selectedCreators}
        onClose={() => setSelectedIds([])}
        onRemove={(id) => setSelectedIds((prev) => prev.filter((x) => x !== id))} />

      }
    </div>);

}